import { useMutation, useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { METER_SCOPE_NAMES } from 'common/src/database/scopes/meters/meterScopeNames';
import { FilterParam } from 'common/src/query-params/FilterParam';
import { SingleOperators } from 'common/src/query-params/operators';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import {
  IAccount,
  IFacility,
  IMeter,
  IPage,
} from 'common/src/types/interfaces';
import {
  ICreateMeterDto,
  IUpdateMeterDto,
  IMeterList,
} from 'common/src/types/meter.dtos';

import { getAccounts } from 'portal/src/api/AccountsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import { getFacilities } from 'portal/src/api/FacilitiesService';
import { addMeter, getMeters, updateMeter } from 'portal/src/api/MetersService';
import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import { BUTTON_TEXT } from 'portal/src/constants/button-text';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { defaultValues, getFields, MeterFormValues } from './MeterForm.fields';

import HistoricalValuesDialogTable from '../HistoricalValues/HistoricalValuesDialogTable';
import { useHistoricalValues } from '../HistoricalValues/useHistoricalValues';

const MeterForm = ({
  backButtonlabel = BUTTON_TEXT.back,
  deactivatedFields = [],
  entity = {},
  handleBackAction,
}: {
  backButtonlabel?: string;
  deactivatedFields?: (keyof IMeter)[];
  entity?: Partial<IMeter>;
  handleBackAction: () => void;
}) => {
  const { featureChecker, hasSystemAdminAccess, getUser } = useAuth();
  const { organizationId } = getUser();
  const snackbarHandler = useSnackbarHandler();

  const defaults = defaultValues(entity);
  const {
    control,
    formState: { dirtyFields, isDirty },
    handleSubmit,
    getValues,
    setError,
    setValue,
    watch,
  } = useForm({ defaultValues: defaults });
  const [serviceType, netMeter, isMaster] = watch([
    'serviceType',
    'netMeter',
    'isMaster',
  ]);
  const { getSnackbarApiErrorMessage } = useFormControlError({
    getValues,
    setError,
  });

  const isDeactivated = entity.id && !entity.isActive;

  const meterFilters: FilterParam[] = entity.id
    ? [
        {
          field: 'id',
          operator: SingleOperators.NOT_EQUAL_TO,
          value: entity.id,
        },
      ]
    : [];

  const [{ data: meters }, { data: accounts }, { data: facilities }] =
    useQueries({
      queries: [
        {
          queryKey: [QUERY_KEYS.meters],
          queryFn: async () =>
            await getMeters({
              scope: METER_SCOPE_NAMES.list,
              filters: meterFilters,
            }),
          initialData: { data: [] } as IPage<IMeterList>,
        },
        {
          queryKey: [QUERY_KEYS.accounts],
          queryFn: async () => await getAccounts(),
          initialData: { data: [] } as IPage<IAccount>,
        },
        {
          queryKey: ['facilities'],
          queryFn: async () => await getFacilities(),
          initialData: { data: [] } as IPage<IFacility>,
        },
      ],
    });

  const { mutate: markReviewed, isPending: isPendingMarkReviewed } =
    useMutation({
      mutationFn: async () =>
        await updateMeter(entity.id, { needsReview: false }),
      onSuccess: () => {
        handleBackAction();
        snackbarHandler.success(MESSAGES.METER.MARKED_REVIEWED);
      },
      onError: () => {
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      },
    });

  const [isPending, setIsPending] = useState(false);

  const historyConfigs = useHistoricalValues(
    defaults.rateSchedule,
    entity.rateScheduleHistoricalValues,
    entity.createdAt,
  );

  const hasActiveTemplate =
    !!entity.parentAccount?.activeAllocationTemplate ||
    !!entity.masterMeter?.parentAccount?.activeAllocationTemplate;
  const allocationsEnabled = featureChecker.productFeatureEnabled(
    ProductFeatures.allocations,
  );

  const formFieldsConfiguration = useMemo(() => {
    const masterMeters = meters.data?.filter((m) => m.isMaster);

    const fields = getFields(
      accounts.data,
      facilities.data,
      meters.data,
      netMeter,
      serviceType,
      hasSystemAdminAccess(),
      !isMaster,
      masterMeters,
      historyConfigs.handleValueChange,
      getValues,
      setValue,
      hasActiveTemplate,
      entity.id ? historyConfigs.handleOpen : null,
      allocationsEnabled,
    );

    if (deactivatedFields.length === 0) return fields;
    return fields.map((f) =>
      deactivatedFields.includes(f.name as keyof IMeter)
        ? { ...f, disabled: true }
        : f,
    );
  }, [
    accounts.data,
    allocationsEnabled,
    deactivatedFields,
    entity.id,
    facilities.data,
    getValues,
    hasActiveTemplate,
    hasSystemAdminAccess,
    isMaster,
    meters.data,
    netMeter,
    historyConfigs.handleOpen,
    historyConfigs.handleValueChange,
    serviceType,
    setValue,
  ]);

  const sendData = async (payload: MeterFormValues) => {
    setIsPending(true);

    const createModeDirtyFields: Partial<
      Readonly<Record<keyof MeterFormValues, boolean>>
    > = {
      parentAccountId: true,
      parentFacilityId: true,
    };
    const formattedFields = {
      ...prepareFormPayload(
        payload,
        entity.id ? dirtyFields : { ...dirtyFields, ...createModeDirtyFields },
      ),
    };
    const formattedPayload = {
      ...formattedFields,
      ...(formattedFields.rateSchedule !== undefined || historyConfigs.isDirty
        ? {
            rateScheduleHistoricalValues: historyConfigs.formatPayload(),
          }
        : {}),
    };

    async function add() {
      await addMeter({
        ...formattedPayload,
        organizationId,
      } as unknown as ICreateMeterDto);
      handleBackAction();
      snackbarHandler.success(MESSAGES.METER.CREATED);
    }

    async function update() {
      await updateMeter(
        entity.id,
        formattedPayload as unknown as IUpdateMeterDto,
      );
      handleBackAction();
      snackbarHandler.success(MESSAGES.METER.UPDATED);
    }

    try {
      await (entity.id ? update() : add());
    } catch (err: any) {
      snackbarHandler.error(getSnackbarApiErrorMessage(err));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <FormContainer
      afterMainFields={
        entity.id && (
          <HistoricalValuesDialogTable
            historyConfigs={historyConfigs}
            targetHeaderName="Rate Schedule"
            targetColumnType="string"
          />
        )
      }
      backButtonlabel={backButtonlabel}
      control={control}
      isDirty={isDirty || historyConfigs.isDirty}
      extraButtons={
        entity.id && entity.needsReview && hasSystemAdminAccess()
          ? [
              {
                onClick: async () => markReviewed(),
                label: BUTTON_TEXT.markReviewed,
              },
            ]
          : undefined
      }
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => sendData(data))}
      header="Meter Information"
      isDeactivated={!!isDeactivated}
      isNew={!entity.id}
      isPending={isPending || isPendingMarkReviewed}
      mainFieldsConfig={formFieldsConfiguration}
    />
  );
};

export default MeterForm;
